/**
 * Top-level tab routing: swim · sets · strokes · club (Scott, 2026-09-10).
 *
 * - `swim`:    what have I been doing (latest swim's story)
 * - `sets`:    your sets across swims, getting faster?
 * - `strokes`: one card per stroke, and the Glide trend
 * - `club`:    the club
 *
 * Scores are not a tab: they are the drawer behind the top-right ring.
 */
import { writable, type Writable } from "svelte/store";

export type TabId = "swim" | "sets" | "strokes" | "club";

/** Tab-bar order. The native shell's TabRoute lists the same four, same order. */
export const TAB_IDS: readonly TabId[] = ["swim", "sets", "strokes", "club"];

/**
 * A tab id that came from outside the page (the shell's user script or its
 * shell-state broadcast). "scores" is what a shell from before the four tabs
 * still pins a view to; that view's content now lives on Sets. Anything else
 * unknown is null, never a guess.
 */
export function parseTabId(raw: unknown): TabId | null {
  if (raw === "scores") return "sets";
  return typeof raw === "string" && (TAB_IDS as readonly string[]).includes(raw) ? (raw as TabId) : null;
}

export const currentTab: Writable<TabId> = writable("swim");

/**
 * Showcase mode — the PUBLIC swimset.club/showcase page. When true, App.svelte
 * wraps the REUSED swim components in coming-soon chrome and hides app-only bits.
 * Only `main.ts` sets it true, when `isShowcaseRequest` says so.
 */
export const showcase: Writable<boolean> = writable(false);

/**
 * Which boot a page gets: the public showcase (true) or the app shell.
 *
 * 1. `/showcase` (trailing slash tolerated, deeper paths not) or `?showcase=1`:
 *    the showcase, always. These are the public share links.
 * 2. The native bridge is present: the app. The native app is told apart by its
 *    bridge, never by the path, because it loads a file URL whose path is the
 *    bundle's own.
 * 3. No bridge in a production build: this is swimset.club in a browser, and
 *    wrangler's SPA fallback serves index.html for every path, so EVERY path is
 *    the showcase. Before this, `/app` booted the full app shell for any visitor.
 * 4. No bridge in a dev build: the showcase at the root only, so local work in a
 *    plain browser can still open the app at `/app`.
 */
export function isShowcaseRequest(
  loc: { pathname: string; search: string },
  nativeShell: boolean,
  dev: boolean,
): boolean {
  const path = loc.pathname.replace(/\/+$/, "").toLowerCase();
  if (path === "/showcase") return true;
  if (new URLSearchParams(loc.search).get("showcase") === "1") return true;
  if (nativeShell) return false;
  return !dev || path === "";
}
